<script setup lang="ts">
import type { PaymentSummaryPayoutErrorsItem } from '@metapic-apps/shared'
import { Card, ConfirmationPopup, formatDateOnly, ProfileImageComponent, TooltipComponent } from '@metapic-apps/shared'
import { computed, ref } from 'vue'

import CheckIcon from '@/assets/icons/check-icon.svg?component'
import CoinIcon from '@/assets/icons/coin.svg?component'
import RemoveCircleIcon from '@/assets/icons/remove-circle.svg?component'
import { formatMoneyForAdmin } from '@/lib/formatters/money.formatter'
import { getRecruitmentUtmLabel } from '@/lib/helpers'
import type { MoneyType } from '@/lib/types/common.types'
import type { CreatorDetail, CreatorStatusAction } from '@/lib/types/creator-detail.types'
import type { UpcomingPayment } from '@/lib/types/payments.types'
import { BoxComponent, FlexComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  creator: CreatorDetail
  currentBalance?: MoneyType | null
  upcomingPayment?: UpcomingPayment | null
  isUpdating: boolean
}>()

const emits = defineEmits<{
  statusChange: [action: CreatorStatusAction]
}>()

const isConfirmOpen = ref(false)
const pendingAction = ref<CreatorStatusAction | null>(null)

const payoutErrorMessages: Record<PaymentSummaryPayoutErrorsItem, string> = {
  THRESHOLD_NOT_REACHED: 'The creator’s earnings have not reached the payout threshold.',
  INCOMPLETE_FINANCIAL_INFORMATION: 'The creator’s financial information is incomplete.',
  INVALID_FINANCIAL_INFORMATION: 'The creator’s financial information is invalid and must be corrected.',
  PAYMENTS_BLOCKED: 'Payments to this creator are blocked.',
  UNSPECIFIED_ERROR: 'The payout cannot be processed. Contact support for more details',
}

const upcomingPaymentTooltipLines = computed(() => {
  const payment = props.upcomingPayment
  if (!payment) {
    return []
  }
  if (payment.isPayable) {
    return [`Payment date: ${payment.paymentDate ? formatDateOnly(payment.paymentDate) : '—'}`]
  }
  return payment.payoutErrors.length
    ? payment.payoutErrors.map((error) => payoutErrorMessages[error])
    : [payoutErrorMessages.UNSPECIFIED_ERROR]
})

const statusOptions: { value: CreatorStatusAction; label: string; color: string }[] = [
  { value: 'notApproved', label: 'Undecided', color: 'var(--secondary-48)' },
  { value: 'approved', label: 'Accepted', color: 'var(--success)' },
  { value: 'banned', label: 'Suspended', color: 'var(--alert)' },
  { value: 'blockPayments', label: 'Block payments', color: 'var(--warning)' },
  { value: 'banAndRemoveEarnings', label: 'Suspend & remove earnings', color: 'var(--alert)' },
]

const currentStatusLabel = (action: CreatorStatusAction) => statusOptions.find((o) => o.value === action)?.label ?? action

const currentStatusColor = (action: CreatorStatusAction) => statusOptions.find((o) => o.value === action)?.color ?? 'var(--secondary-48)'

const creatorName = () => {
  if (props.creator.firstName || props.creator.lastName) {
    return [props.creator.firstName, props.creator.lastName].filter(Boolean).join(' ')
  }
  return props.creator.username
}

const recruitmentUtmLabel = computed(() => getRecruitmentUtmLabel(props.creator))

const recruitmentCampaign = computed(() => props.creator.recruitmentData?.campaign ?? null)

const onStatusSelect = (action: CreatorStatusAction) => {
  if (action === props.creator.statusAction) {
    return
  }
  pendingAction.value = action
  isConfirmOpen.value = true
}

const confirmStatusChange = () => {
  if (pendingAction.value) {
    emits('statusChange', pendingAction.value)
  }
  isConfirmOpen.value = false
  pendingAction.value = null
}
</script>
<template>
  <Card class="creator-header">
    <FlexComponent align="center" :gap="24">
      <BoxComponent class="creator-header__avatar">
        <ProfileImageComponent :image="creator.avatar" :name="creator.username" :alt="creator.username" :size="80" />
      </BoxComponent>
      <FlexComponent direction="column" :gap="4" class="creator-header__info">
        <TypographyComponent variant="headline-small">
          {{ creatorName() }}
        </TypographyComponent>
        <TypographyComponent variant="body-small" color="var(--secondary-48)">
          Registered {{ formatDateOnly(creator.createdAt) }}
        </TypographyComponent>
        <TypographyComponent
          v-if="recruitmentUtmLabel"
          variant="body-small"
          color="var(--secondary-48)"
          class="creator-header__recruitment"
          text-truncate
        >
          Recruitment:
          <router-link
            v-if="recruitmentCampaign"
            :to="{ path: '/campaigns', query: { campaignId: recruitmentCampaign.id } }"
            class="creator-header__recruitment-link"
          >
            {{ recruitmentUtmLabel }}
          </router-link>
          <template v-else>{{ recruitmentUtmLabel }}</template>
        </TypographyComponent>
      </FlexComponent>
      <BoxComponent class="creator-header__balance-wrapper">
        <FlexComponent align="center" :gap="8" class="creator-header__balances">
          <FlexComponent align="center" :gap="8" class="creator-header__balance">
            <el-icon :size="18" class="creator-header__coin-icon">
              <CoinIcon />
            </el-icon>
            <TypographyComponent variant="label-large"> Current balance: {{ formatMoneyForAdmin(currentBalance) }} </TypographyComponent>
          </FlexComponent>
          <TooltipComponent v-if="upcomingPayment" placement="top">
            <template #default>
              <FlexComponent
                align="center"
                :gap="8"
                class="creator-header__balance creator-header__upcoming-payment"
                :aria-label="upcomingPaymentTooltipLines.join(' ')"
              >
                <el-icon
                  :size="18"
                  class="creator-header__payment-status-icon"
                  :class="
                    upcomingPayment.isPayable
                      ? 'creator-header__payment-status-icon--payable'
                      : 'creator-header__payment-status-icon--not-payable'
                  "
                >
                  <CheckIcon v-if="upcomingPayment.isPayable" />
                  <RemoveCircleIcon v-else />
                </el-icon>
                <TypographyComponent variant="label-large">
                  Upcoming payment: {{ formatMoneyForAdmin(upcomingPayment.amount) }}
                </TypographyComponent>
              </FlexComponent>
            </template>
            <template #content>
              <div v-for="(message, index) in upcomingPaymentTooltipLines" :key="index">{{ message }}</div>
            </template>
          </TooltipComponent>
        </FlexComponent>
      </BoxComponent>
      <BoxComponent class="creator-header__divider" />
      <el-dropdown trigger="click" :teleported="false" placement="bottom-end">
        <button class="creator-header__status-btn" :style="{ backgroundColor: currentStatusColor(creator.statusAction) }">
          <TypographyComponent variant="label-large" color="var(--inverted)">
            {{ currentStatusLabel(creator.statusAction) }}
          </TypographyComponent>
          <el-icon :size="18" color="var(--inverted)">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </el-icon>
        </button>
        <template #dropdown>
          <BoxComponent :mt="4" :mb="4">
            <el-dropdown-item v-for="option in statusOptions" :key="option.value" @click="onStatusSelect(option.value)">
              <TypographyComponent variant="body-large">
                {{ option.label }}
              </TypographyComponent>
            </el-dropdown-item>
          </BoxComponent>
        </template>
      </el-dropdown>
    </FlexComponent>
  </Card>
  <ConfirmationPopup
    v-model="isConfirmOpen"
    :is-loading="isUpdating"
    title="Change creator status"
    :description="`Are you sure you want to change the status to '${pendingAction ? currentStatusLabel(pendingAction) : ''}'?`"
    submit-btn-text="Confirm"
    submit-btn-variant="primary"
    @on-submit="confirmStatusChange"
  />
</template>
<style scoped lang="scss">
.creator-header__avatar {
  flex-shrink: 0;

  :deep(img),
  :deep(.el-avatar) {
    border-radius: 50%;
  }
}

.creator-header__info {
  flex: 1;
  min-width: 0;
}

.creator-header__recruitment {
  display: block;
  max-width: 100%;
}

.creator-header__recruitment-link {
  color: var(--primary);

  &:hover {
    text-decoration: underline;
  }
}

.creator-header__balance-wrapper {
  flex-shrink: 0;
}

.creator-header__balance {
  padding: 8px 24px 8px 16px;
  border: 1px solid var(--secondary-8);
  border-radius: 40px;
  height: 40px;
}

.creator-header__balances {
  flex-wrap: wrap;
}

.creator-header__upcoming-payment {
  cursor: help;
}

.creator-header__payment-status-icon {
  flex-shrink: 0;

  :deep(path) {
    fill: currentColor;
  }
}

.creator-header__payment-status-icon--payable {
  color: var(--success);
}

.creator-header__payment-status-icon--not-payable {
  color: var(--alert);
}

.creator-header__coin-icon {
  flex-shrink: 0;

  :deep(path) {
    fill: currentColor;
  }
}

.creator-header__divider {
  width: 1px;
  align-self: stretch;
  background-color: var(--secondary-8);
}

.creator-header__status-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 8px 24px;
  border: none;
  border-radius: 40px;
  cursor: pointer;
  height: 40px;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  :deep(path) {
    fill: currentColor;
  }
}
</style>
